'use client'

import React, { useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { ArrowRight, ShieldCheck, Tag, Truck, Zap } from 'lucide-react'
import { discardHTMLTags, getBaseImageUrl, getCurrencySymbol, getFinalDiscountTag, getRandomStoreSeoTitle } from '@/constants/hooks'
import { Offer } from '@/services/dataTypes'
import ExpiryTag from './ExpiryTag'
import DiscountTag from './DiscountTag'
import OfferDetailsToggle from './OfferDetailsToggle'
import Theme18OfferLink from './Theme18OfferLink'
import { copyToClipboard } from '../utils/clipboard'

const C = {
    PRIMARY: '#F8F246',
    SECONDARY: '#2CBBC3',
    BROWN: '#8C5826',
    BORDER: '#f0ede8',
    TEXT: '#1a1a1a',
    MUTED: '#6b7280',
    ORANGE: '#FF9934',
}

interface Props {
    product: Offer
    domain: string
    merchantHref: string
    merchant_name: string
    merchant_logo: string
    type?: string
    productDetailUrl?: string | null
}

const EventOfferCard = ({ product, merchantHref, domain, merchant_name, merchant_logo, type, productDetailUrl }: Props) => {
    const [copied, setCopied] = useState(false)

    const imageUrl = getBaseImageUrl(domain, merchant_logo, '')
    const imageSrc = (product?.offer_type?.name === 'product' && product?.product_image)
        ? getBaseImageUrl(domain, product.product_image, '') : imageUrl

    const originalPrice = product?.original_price ? parseFloat(product.original_price) : 0
    const salePrice = product?.sale_price ? parseFloat(product.sale_price) : 0
    const discountPercent = originalPrice > 0 && salePrice > 0
        ? Math.round(((originalPrice - salePrice) / originalPrice) * 100) : null
    const finalDiscountTag = getFinalDiscountTag(product?.offer_title || product?.offer_detail, discountPercent)
    const currencySymbol = getCurrencySymbol(product?.currency)
    const isCoupon = !!product?.coupon_code
    const isFreeShipping = product?.keywords?.toLowerCase().includes('free shipping') || product?.offer_detail?.toLowerCase().includes('free shipping')

    const handleCopy = async (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (product?.coupon_code) {
            await copyToClipboard(product.coupon_code.trim())
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        }
    }

    return (
        <article
            className="group bg-white rounded-3xl overflow-hidden flex flex-col h-full transition-all duration-300 hover:-translate-y-1 hover:shadow-xl border border-[#f0ede8] hover:border-[#8C5826]/30"
            style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.03)' }}
        >
            {/* ── Top Image & Badges Stage ── */}
            <div className="relative h-40 sm:h-44 w-full bg-gradient-to-br from-[#fffdfa] via-[#fcfbf9] to-[#f7f5f0] border-b border-[#f0ede8] flex items-center justify-center p-3 sm:p-4 overflow-hidden flex-shrink-0">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-50/20 to-teal-50/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                {/* Product Image */}
                <div className="relative w-28 h-28 sm:w-32 sm:h-32 flex items-center justify-center z-10">
                    <Image
                        src={imageSrc}
                        alt={discardHTMLTags(product?.offer_title) || merchant_name || 'Product'}
                        width={120}
                        height={120}
                        className="object-contain max-h-28 sm:max-h-32 w-auto group-hover:scale-105 transition-transform duration-300 drop-shadow-xs"
                        loading="lazy"
                    />
                </div>

                {/* Top Left: Discount Tag */}
                {finalDiscountTag && (
                    <div className="absolute top-2.5 left-2.5 z-20">
                        <DiscountTag tag={finalDiscountTag} />
                    </div>
                )}

                {/* Top Right: Code / Deal Badge */}
                <div className="absolute top-2.5 right-2.5 z-20">
                    {isCoupon ? (
                        <span className="inline-flex items-center gap-1 text-[9px] font-black px-2 py-0.5 rounded-lg shadow-xs tracking-wider"
                            style={{ background: C.PRIMARY, color: C.BROWN }}>
                            <Tag size={9} /> CODE
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1 text-[9px] font-black px-2 py-0.5 rounded-lg shadow-xs tracking-wider"
                            style={{ background: '#e0f7f9', color: C.SECONDARY }}>
                            <Zap size={9} /> DEAL
                        </span>
                    )}
                </div>

                {/* Bottom Left: Free Shipping Badge */}
                {isFreeShipping && (
                    <div className="absolute bottom-2.5 left-2.5 z-20">
                        <span className="inline-flex items-center gap-1 text-[8px] font-bold px-1.5 py-0.5 rounded-md shadow-xs"
                            style={{ background: C.SECONDARY, color: '#ffffff' }}>
                            <Truck size={9} /> Free Shipping
                        </span>
                    </div>
                )}
            </div>

            {/* ── Content Section ── */}
            <div className="p-4 flex flex-col flex-1 justify-between gap-2.5">
                <div className="space-y-2">
                    {/* Merchant Row */}
                    <div className="flex items-center justify-center gap-1 mb-1">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                            {merchant_name}
                        </span>
                    </div>

                    {/* Title (Centered) */}
                    {product?.is_detail === 1 && productDetailUrl ? (
                        <Link
                            href={productDetailUrl}
                            className="block text-xs sm:text-sm font-bold leading-snug line-clamp-2 min-h-[2.5rem] text-center text-gray-900 hover:text-[#8C5826] transition-colors"
                        >
                            {discardHTMLTags(product?.offer_title)}
                        </Link>
                    ) : (
                        <Theme18OfferLink
                            unique_id={product?.unique_id}
                            outUrl={product?.url || merchantHref}
                            merchantHref={merchantHref}
                            domain={domain}
                            couponCode={product?.coupon_code || undefined}
                            customClass="block"
                        >
                            <h3 className="text-xs sm:text-sm font-bold leading-snug line-clamp-2 min-h-[2.5rem] text-center text-gray-900 hover:text-[#8C5826] transition-colors">
                                {discardHTMLTags(product?.offer_title)}
                            </h3>
                        </Theme18OfferLink>
                    )}
                </div>

                {/* ── Bottom Info & CTA ── */}
                <div className="flex flex-col gap-3 pt-3 border-t border-[#f0ede8] mt-auto">
                    {/* Price Row (Centered) */}
                    {(salePrice > 0 || originalPrice > 0) && (
                        <div className="flex items-baseline justify-center gap-2">
                            <span className="text-xl font-black text-gray-900">
                                {currencySymbol}{(salePrice > 0 ? salePrice : originalPrice).toFixed(2)}
                            </span>
                            {originalPrice > 0 && salePrice > 0 && originalPrice > salePrice && (
                                <span className="text-xs line-through text-gray-400 font-medium">
                                    {currencySymbol}{originalPrice.toFixed(2)}
                                </span>
                            )}
                            {discountPercent && discountPercent > 0 && (
                                <span className="text-[10px] font-black px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-600 border border-emerald-100">
                                    Save {discountPercent}%
                                </span>
                            )}
                        </div>
                    )}

                    {/* Expiry + Details Toggle */}
                    <div className="flex items-center justify-between gap-2 text-xs">
                        <ExpiryTag endDate={product?.end_date} />
                        <OfferDetailsToggle
                            offer={product}
                            merchantHref={merchantHref}
                            imageSrc={product?.product_image ?? ''}
                            merchantImg={merchant_logo ?? ''}
                            domain={domain}
                            type="anchor"
                            modalType="details"
                        />
                    </div>



                    {/* Primary CTA Button */}
                    <Theme18OfferLink
                        unique_id={product?.unique_id}
                        outUrl={product?.url || merchantHref}
                        merchantHref={merchantHref}
                        domain={domain}
                        couponCode={product?.coupon_code || undefined}
                        customClass="w-full block"
                    >
                        <div
                            className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-2xl text-xs font-black uppercase tracking-wider transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] shadow-sm hover:shadow-md cursor-pointer"
                            style={isCoupon
                                ? { background: C.BROWN, color: '#ffffff', boxShadow: `0 4px 12px rgba(140, 88, 38, 0.25)` }
                                : { background: `linear-gradient(135deg, ${C.PRIMARY} 0%, #ffd452 100%)`, color: C.BROWN, border: '1px solid rgba(140, 88, 38, 0.15)', boxShadow: `0 4px 12px rgba(248, 242, 70, 0.35)` }}
                        >
                            <span>{isCoupon ? 'Show Code' : (type === 'product' ? 'Buy Now' : 'Get Deal')}</span>
                            <ArrowRight size={13} />
                        </div>
                    </Theme18OfferLink>
                </div>
            </div>
        </article>
    )
}

export default EventOfferCard
